"use client";

import { useEffect, useRef } from "react";
import type { MuseumExperience } from "@/lib/museum/types";
import styles from "./museum.module.css";

export function JourneyMap({
  movements,
  activeIndex,
  journey,
}: {
  movements: { id: string; title: string; threadLabel: string }[];
  activeIndex: number;
  journey: MuseumExperience["journey"];
}) {
  const listRef = useRef<HTMLOListElement>(null);
  const active = movements[activeIndex] ?? movements[0];

  useEffect(() => {
    const list = listRef.current;
    const node = list?.querySelector<HTMLElement>(`[data-timeline-node="${active?.id}"]`);
    if (!list || !node || list.scrollWidth <= list.clientWidth) return;
    list.scrollLeft = node.offsetLeft - list.clientWidth / 2 + node.clientWidth / 2;
  }, [active?.id]);

  return (
    <nav aria-label="History timeline" className={styles.timeline}>
      <div className={styles.timelineHeading}>
        <span>The Archive of Days</span>
        <strong data-active-date>{active?.threadLabel}</strong>
      </div>
      <div
        className={[
          styles.ropeWrap,
          journey === "tracks" ? styles.trackWrap : "",
          journey !== "rope" && journey !== "tracks" ? styles.courseWrap : "",
        ].filter(Boolean).join(" ")}
        aria-hidden="true"
        data-timeline-path={journey}
        data-timeline-rope={journey === "rope" ? "" : undefined}
        data-timeline-tracks={journey === "tracks" ? "" : undefined}
      >
        <span className={styles.ropeBase} />
        <span className={styles.ropeTravelled} />
      </div>
      <ol ref={listRef} className={styles.timelineList}>
        {movements.map((movement, index) => (
          <li key={movement.id} className={styles.timelineItem}>
            <a
              href={`#${movement.id}`}
              aria-label={`${movement.threadLabel}: ${movement.title}`}
              data-timeline-node={movement.id}
              aria-current={index === activeIndex ? "step" : undefined}
              className={styles.timelineLink}
            >
              <span className={styles.timelineDot} aria-hidden="true" />
              <span className={styles.timelineDate}>{movement.threadLabel}</span>
              <span className={styles.timelineTitle}>{movement.title}</span>
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}
